import * as React from 'react'
import { clsx } from 'clsx'

import { Badge } from './Badge'
import type { BadgeProps } from './Badge'
import { useLocalStorage } from '../../hooks/useLocalStorage'

import styles from './NewBadge.module.css'

const DAY_MS = 24 * 60 * 60 * 1000
// setTimeout's delay is a 32-bit signed int; a multi-week deadline must be
// chunked into waits this size or shorter, or it fires immediately.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

type NewBadgeExpirationProps =
  | {
      /**
       * Unique key used to track, per user, when this badge first appeared.
       * Required alongside `expireAfterDays`.
       */
      id?: undefined
      /** Hides the badge once this many days have passed since it first appeared for the current user. */
      expireAfterDays?: undefined
    }
  | {
      id: string
      expireAfterDays: number
    }

type NewBadgeProps = Omit<BadgeProps, 'children' | 'size'> &
  NewBadgeExpirationProps & {
    /** Hides the badge once this fixed date has passed, for every user. */
    expiresAt?: string | number | Date
  }

const NewBadge = React.forwardRef<HTMLSpanElement, NewBadgeProps>(
  ({ className, id, expireAfterDays, expiresAt, ...props }, forwardedRef) => {
    // Hooks can't be called conditionally, so this key is read/written even
    // when `id` is unset; the value is simply never used in that case.
    const [firstSeenAt, setFirstSeenAt] = useLocalStorage<number | null>(
      `popcorn-new-badge:${id ?? 'unkeyed'}`,
      null,
    )

    React.useEffect(() => {
      if (id && firstSeenAt === null) {
        setFirstSeenAt(Date.now())
      }
    }, [id, firstSeenAt, setFirstSeenAt])

    const isPastFixedExpiry =
      expiresAt !== undefined && Date.now() > new Date(expiresAt).getTime()

    const isPastUserExpiry =
      id !== undefined &&
      firstSeenAt !== null &&
      Date.now() - firstSeenAt > expireAfterDays * DAY_MS

    const hasExpired = isPastFixedExpiry || isPastUserExpiry

    // Re-render at the next deadline so the badge hides itself even if
    // nothing else causes this component to re-render in the meantime. A
    // deadline beyond MAX_TIMEOUT_MS is reached by rescheduling on each tick.
    const [recheckTick, scheduleRecheck] = React.useReducer(
      (tick: number) => tick + 1,
      0,
    )

    React.useEffect(() => {
      if (hasExpired) {
        return
      }

      const deadlines = [
        expiresAt !== undefined ? new Date(expiresAt).getTime() : undefined,
        id !== undefined && firstSeenAt !== null
          ? firstSeenAt + expireAfterDays * DAY_MS
          : undefined,
      ].filter((deadline): deadline is number => Number.isFinite(deadline))

      if (deadlines.length === 0) {
        return
      }

      const delay = Math.min(
        Math.max(Math.min(...deadlines) - Date.now(), 0),
        MAX_TIMEOUT_MS,
      )
      const timer = setTimeout(scheduleRecheck, delay)
      return (): void => clearTimeout(timer)
    }, [hasExpired, expiresAt, id, expireAfterDays, firstSeenAt, recheckTick])

    if (hasExpired) {
      return null
    }

    return (
      <Badge
        {...props}
        size="small"
        className={clsx(className, styles.new)}
        ref={forwardedRef}
      >
        New
      </Badge>
    )
  },
)

NewBadge.displayName = 'NewBadge'

export { NewBadge }
export type { NewBadgeProps }
